import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { opsApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';
import { ShoppingBag, Package, CreditCard, AlertTriangle, Activity } from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, color = 'bg-blue-500', to }) {
  const inner = (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${color} text-white flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
        <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function OpsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    opsApi.dashboard().then(r => setData(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { orders, revenue, inventory, payments, webhooks } = data;

  return (
    <div data-testid="ops-dashboard" className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Operations Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Real-time overview of commerce operations</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={ShoppingBag} label="Total Orders" value={orders.total} sub={`${orders.confirmed} confirmed, ${orders.processing} processing`} color="bg-blue-600" to="/ops/orders" />
        <StatCard icon={Activity} label="Revenue" value={formatCurrency(revenue.total)} sub={`${revenue.orderCount} paid orders`} color="bg-green-600" />
        <StatCard icon={Package} label="Active Products" value={inventory.activeProducts} sub={`${inventory.lowStock} low stock, ${inventory.outOfStock} out of stock`} color="bg-purple-600" to="/ops/inventory" />
        <StatCard icon={CreditCard} label="Payments" value={payments.paid} sub={`${payments.open} open, ${payments.failed} failed`} color="bg-amber-600" to="/ops/payments" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order status breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Order Status Breakdown</h2>
          <div className="space-y-3">
            {[
              { label: 'Pending Payment', count: orders.pending, color: 'bg-yellow-400' },
              { label: 'Confirmed', count: orders.confirmed, color: 'bg-blue-400' },
              { label: 'Processing', count: orders.processing, color: 'bg-blue-600' },
              { label: 'Shipped', count: orders.shipped, color: 'bg-purple-500' },
              { label: 'Delivered', count: orders.delivered, color: 'bg-green-500' },
              { label: 'Cancelled', count: orders.cancelled, color: 'bg-red-400' },
            ].map(s => (
              <div key={s.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2"><div className={`w-2.5 h-2.5 rounded-full ${s.color}`} /><span className="text-gray-700">{s.label}</span></div>
                <span className="font-mono font-semibold text-gray-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Alerts</h2>
          <div className="space-y-3">
            {inventory.outOfStock > 0 && (
              <div className="flex items-center gap-3 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-red-800"><strong>{inventory.outOfStock}</strong> product(s) out of stock</span>
              </div>
            )}
            {inventory.lowStock > 0 && (
              <div className="flex items-center gap-3 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-amber-800"><strong>{inventory.lowStock}</strong> product(s) low stock (≤5)</span>
              </div>
            )}
            {payments.failed > 0 && (
              <div className="flex items-center gap-3 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-red-800"><strong>{payments.failed}</strong> failed/cancelled payment(s)</span>
              </div>
            )}
            {webhooks.errors > 0 && (
              <div className="flex items-center gap-3 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-amber-800"><strong>{webhooks.errors}</strong> webhook error(s)</span>
              </div>
            )}
            {inventory.outOfStock === 0 && inventory.lowStock === 0 && payments.failed === 0 && webhooks.errors === 0 && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">All systems nominal. No alerts.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
